import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'

/**
 * EP-157 T157-005 — one account's own white-label app registration
 * (docs/ep-157-app-integration-push-credentials.md §3.1, §8 A1-A4). Singular
 * resource, same shape as `useAccountSettings`/`useSubscription`: GET/PUT/
 * DELETE never carry an id.
 *
 * `firebase_service_account_json` is write-only end to end — the API never
 * returns it, only `service_account_present` + `firebase_client_email`. This
 * file never types a field for the key itself on the read shape, only on the
 * update input, so a read response can never be mistaken for carrying it.
 */
export interface AppIntegration {
  id: number
  name: string
  ios_bundle_id: string | null
  android_package_name: string | null
  firebase_project_id: string
  service_account_present: boolean
  firebase_client_email: string | null
  verified_at: string | null
}

export const appIntegrationKeys = {
  all: ['appIntegration'] as const,
  detail: () => [...appIntegrationKeys.all, 'detail'] as const,
}

export function useAppIntegration() {
  return useQuery({
    queryKey: appIntegrationKeys.detail(),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<AppIntegration | null>>('/api/app_integration')
      return data.data
    },
  })
}

export interface UpdateAppIntegrationInput {
  name: string
  ios_bundle_id?: string
  android_package_name?: string
  firebase_project_id: string
  // Optional on update (docs §8 A2): omit (or send blank) to keep the stored
  // key. The caller only sends this when the operator chose a new .json file.
  firebase_service_account_json?: string
}

export function useUpdateAppIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateAppIntegrationInput) => {
      const { data } = await api.put<ApiResponse<AppIntegration>>('/api/app_integration', {
        app_integration: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(appIntegrationKeys.detail(), data)
    },
  })
}

export function useDeleteAppIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.delete('/api/app_integration')
    },
    onSuccess: () => {
      queryClient.setQueryData(appIntegrationKeys.detail(), null)
    },
  })
}

export interface AppIntegrationConnectionChecks {
  key_valid: boolean
  fcm_reachable: boolean
}

export interface AppIntegrationConnectionResult {
  ok: boolean
  project_id: string
  checks: AppIntegrationConnectionChecks
}

export function useTestAppIntegrationConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ApiResponse<AppIntegrationConnectionResult>>(
        '/api/app_integration/test_connection',
      )
      return data.data
    },
    onSuccess: () => {
      // The server stamps verified_at on success; refetch rather than guess
      // the shape (mirrors LineOaSettingsCard's test-connection invalidation).
      queryClient.invalidateQueries({ queryKey: appIntegrationKeys.detail() })
    },
  })
}
